import { computed } from 'mobx'
import type { MobxDocument } from '../firestore-mobx/document'
import {
  ObservableModelWithDecorators,
  emptyCollection,
  emptyModel,
} from '../firestore-mobx/model'
import { empty, type FirestoreSlideDeck } from '../firestore/SlideDeck/schema'
import type { FirebaseRepository } from './FirebaseRepository'
import { SlideDeckState, SlideDeckTheme, SlideDeckType } from '../types'

export { SlideDeckState }

export class SlideDeck extends ObservableModelWithDecorators<FirestoreSlideDeck> {
  static empty(repository: FirebaseRepository) {
    return emptyModel(repository, this, empty)
  }

  static emptyCollection(repository: FirebaseRepository) {
    return emptyCollection(repository, this, empty)
  }

  constructor(
    repository: FirebaseRepository,
    doc: MobxDocument<FirestoreSlideDeck>
  ) {
    super(repository, doc)
  }

  @computed
  get isAutomaticallyArchived() {
    return this.isPublished && this.data.catalogIds.length === 0
  }

  @computed
  get isArchived() {
    return this.slideDeckStatus === SlideDeckStatus.archived
  }

  @computed
  get isPublished() {
    return this.data.slideDeckState === SlideDeckState.published
  }

  /** Deck-level markdown theme applied to all markdown slide surfaces.
   * Returns the enum (not a boolean) so call sites stay theme-extensible. */
  @computed
  get slideDeckTheme(): SlideDeckTheme {
    return this.data.slideDeckTheme
  }

  @computed
  get readOnly() {
    return this.data.slideDeckState >= SlideDeckState.published
  }

  @computed
  get slideDeckStatus() {
    const rawState = this.data.slideDeckState

    if (this.data.slideDeckType === SlideDeckType.demo) {
      // specific logic for demo slide decks - we infer status from featured rather than catalogs

      if (rawState === SlideDeckState.draft) return SlideDeckStatus.draft

      if (this.data.slideDeckFeatured) return SlideDeckStatus.published

      return SlideDeckStatus.archived
    } else {
      // return hidden if published and no catalogs
      if (this.isAutomaticallyArchived) return SlideDeckStatus.archived

      if (rawState === SlideDeckState.hidden) {
        return SlideDeckStatus.hidden
      } else if (rawState === SlideDeckState.deleted) {
        return SlideDeckStatus.hidden
      } else if (rawState === SlideDeckState.uninitialized) {
        return SlideDeckStatus.hidden
      } else if (rawState === SlideDeckState.draft) {
        return SlideDeckStatus.draft
      } else {
        return SlideDeckStatus.published
      }
    }
  }

  @computed
  get priceInDollars() {
    const amount = this.data.slideDeckPrice * 0.1
    // Check if the amount is an integer
    if (Number.isInteger(amount)) {
      return `${amount}`
    } else {
      return `${amount.toFixed(2)}`
    }
  }

  safeFileName() {
    // if no defined file name, make one
    const safeName = (this.data.slideDeckName || 'slide-deck')
      .replace(/[^a-zA-Z0-9._-]/g, '_')
      .replace(/_{2,}/g, '_')
      .replace(/^_+|_+$/g, '')
      .substring(0, 255)

    return safeName
  }
}

export enum SlideDeckStatus {
  draft = 'draft',
  published = 'published',
  archived = 'archived',
  hidden = 'hidden', // Does not appear in the UI
}
